import { useEffect, useMemo, useState } from "react";
import { useRouter } from "next/router";
import { ArrowDown } from "@/components/ui/icons";
import Button from "@/components/ui/design-system/Button";
import Dropdown from "@/components/ui/design-system/Dropdown";
import { type Library } from "@/utils/types";
import { Link } from "@/components/ui/design-system/Link";
import { getTemplateGroupByPath } from "./utils";
import { useTemplateLibrary } from "./data";

export function TemplateLibraryBreadcrumbs({
  home = "/library",
  homeLabel = "Template Library",
  groupPath = [],
}: {
  home?: string;
  homeLabel?: string;
  groupPath?: string[];
}) {
  const router = useRouter();
  const templateLibrary = useTemplateLibrary();

  const navigateToLibraryHome = (): void => {
    router.push({ query: {} });
  };

  const navigateToGroup = (newPath: string) => {
    router.push({ query: { group: newPath } });
  };

  useEffect(() => {
    if (templateLibrary && groupPath.length) {
      if (!getTemplateGroupByPath(templateLibrary.library, groupPath)) {
        router.push({ query: {} });
      }
    }
  }, [templateLibrary, router, groupPath]);

  return (
    <nav data-testid="template-library-breadcrumbs">
      <ul className="flex items-center flex-wrap min-h-ds-8">
        {templateLibrary && groupPath?.length > 0 ? (
          <>
            <li className="pr-2">
              <button onClick={navigateToLibraryHome}>{homeLabel}</button>{" "}
              <span className="pl-1">/</span>
            </li>

            {groupPath?.map((pathPart, index) => (
              <li key={pathPart}>
                {index === groupPath.length - 1 ? (
                  <div className="-ml-2">
                    <TemplateGroupDropdown
                      library={templateLibrary.library}
                      groupPath={groupPath}
                      onGroupSelect={(newPath) => navigateToGroup(newPath)}
                    />
                  </div>
                ) : (
                  <>
                    <span className="pr-2">
                      <Link
                        href={`${home}?group=${groupPath.slice(0, index + 1).join(".")}`}
                      >
                        {pathPart}
                      </Link>
                    </span>
                    <span className="pr-2">/</span>
                  </>
                )}
              </li>
            ))}
          </>
        ) : (
          <li>{homeLabel}</li>
        )}
      </ul>
    </nav>
  );
}

function TemplateGroupDropdown({
  library,
  groupPath,
  onGroupSelect,
}: {
  library?: Library["library"];
  groupPath?: string[];
  onGroupSelect: (val: string) => unknown;
}) {
  const [isOpen, setIsOpen] = useState(false);

  const currentGroup = groupPath?.[groupPath.length - 1];
  const parentPath = useMemo(
    () => groupPath && groupPath.slice(0, groupPath?.length - 1),
    [groupPath]
  );

  const dropdownOptions = useMemo(() => {
    if (!library || !parentPath) return [];

    const parentGroup = parentPath.length
      ? getTemplateGroupByPath(library, parentPath)
      : { children: library };

    if (!parentGroup || !("children" in parentGroup)) return [];

    const filteredGroups = Object.keys(parentGroup.children).sort((a, b) =>
      a.localeCompare(b)
    );

    return filteredGroups.map((group) => ({
      label: group,
      value: group,
      onClick: () => {
        onGroupSelect([...parentPath, group].join("."));
        setIsOpen(false);
      },
    }));
  }, [library, onGroupSelect, parentPath]);

  if (dropdownOptions.length === 1)
    return <span className="pl-2">{dropdownOptions[0]?.label}</span>;

  return (
    <Dropdown
      isOpen={isOpen}
      options={dropdownOptions}
      onClickOutside={() => setIsOpen(false)}
      role={"config-library-dropdown"}
      trigger={
        <Button
          size="small"
          className="!text-base"
          onClick={() => setIsOpen(true)}
          variant={"link_dark"}
          iconPosition={"right"}
          icon={<ArrowDown />}
        >
          {currentGroup}
        </Button>
      }
    />
  );
}
